import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface KBArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  category: string;
  subcategory: string | null;
  tags: string[] | null;
  icon: string;
  is_published: boolean;
  sort_order: number;
  parent_id: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface KBArticleInsert {
  title: string;
  slug: string;
  content: string;
  category: string;
  subcategory?: string | null;
  tags?: string[] | null;
  icon?: string;
  is_published?: boolean;
  sort_order?: number;
  parent_id?: string | null;
}

export interface KBCategory {
  category: string;
  count: number;
  subcategories: { subcategory: string | null; count: number }[];
}

// Generate slug from title
export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

// Fetch all articles
export const useArticles = (category?: string) => {
  return useQuery({
    queryKey: ['kb-articles', category],
    queryFn: async () => {
      let query = supabase
        .from('knowledge_base_articles')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('title', { ascending: true });

      if (category) {
        query = query.eq('category', category);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as KBArticle[];
    },
  });
};

// Fetch playbooks (articles without parent_id that are category 'playbook')
export const usePlaybooks = () => {
  return useQuery({
    queryKey: ['kb-playbooks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_base_articles')
        .select('*')
        .eq('category', 'playbook')
        .is('parent_id', null)
        .order('sort_order', { ascending: true })
        .order('title', { ascending: true });

      if (error) throw error;
      return data as KBArticle[];
    },
  });
};

// Fetch child articles for a playbook
export const usePlaybookChildren = (parentId: string | undefined) => {
  return useQuery({
    queryKey: ['kb-playbook-children', parentId],
    queryFn: async () => {
      if (!parentId) return [];
      
      const { data, error } = await supabase
        .from('knowledge_base_articles')
        .select('*')
        .eq('parent_id', parentId)
        .order('sort_order', { ascending: true })
        .order('title', { ascending: true });

      if (error) throw error;
      return data as KBArticle[];
    },
    enabled: !!parentId,
  });
};

// Fetch single article by slug
export const useArticle = (slug: string | undefined) => {
  return useQuery({
    queryKey: ['kb-article', slug],
    queryFn: async () => {
      if (!slug) return null;
      
      const { data, error } = await supabase
        .from('knowledge_base_articles')
        .select('*')
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data as KBArticle;
    },
    enabled: !!slug,
  });
};

// Normalize text for accent-insensitive search
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
};

// Search articles (accent-insensitive)
export const useSearchArticles = (query: string) => {
  return useQuery({
    queryKey: ['kb-search', query],
    queryFn: async () => {
      if (!query || query.length < 2) return [];

      // Fetch all published articles
      const { data, error } = await supabase
        .from('knowledge_base_articles')
        .select('*')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // Normalize the search query
      const normalizedQuery = normalizeText(query);

      // Filter client-side with accent-insensitive comparison
      return (data || []).filter((article) => {
        const normalizedTitle = normalizeText(article.title);
        const normalizedContent = normalizeText(article.content || '');
        return (
          normalizedTitle.includes(normalizedQuery) ||
          normalizedContent.includes(normalizedQuery)
        );
      }) as KBArticle[];
    },
    enabled: query.length >= 2,
  });
};

// Get categories with counts
export const useCategories = () => {
  return useQuery({
    queryKey: ['kb-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('knowledge_base_articles')
        .select('category, subcategory');

      if (error) throw error;

      // Group by category
      const categoryMap = new Map<string, { count: number; subcategories: Map<string | null, number> }>();
      
      (data || []).forEach((article) => {
        if (!categoryMap.has(article.category)) {
          categoryMap.set(article.category, { count: 0, subcategories: new Map() });
        }
        const cat = categoryMap.get(article.category)!;
        cat.count++;
        
        const subCount = cat.subcategories.get(article.subcategory) || 0;
        cat.subcategories.set(article.subcategory, subCount + 1);
      });

      const categories: KBCategory[] = [];
      categoryMap.forEach((value, key) => {
        const subcategories: { subcategory: string | null; count: number }[] = [];
        value.subcategories.forEach((count, sub) => {
          subcategories.push({ subcategory: sub, count });
        });
        categories.push({ category: key, count: value.count, subcategories });
      });

      return categories;
    },
  });
};

// Create article
export const useCreateArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (article: KBArticleInsert) => {
      const { data: user } = await supabase.auth.getUser();
      
      // Get app_user_id
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.user?.id)
        .single();

      const { data, error } = await supabase
        .from('knowledge_base_articles')
        .insert({
          ...article,
          created_by: appUser?.id,
          updated_by: appUser?.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as KBArticle;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb-articles'] });
      queryClient.invalidateQueries({ queryKey: ['kb-categories'] });
      queryClient.invalidateQueries({ queryKey: ['kb-playbooks'] });
      queryClient.invalidateQueries({ queryKey: ['kb-playbook-children'] });
    },
  });
};

// Update article
export const useUpdateArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<KBArticle> & { id: string }) => {
      const { data: user } = await supabase.auth.getUser();
      
      // Get app_user_id
      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', user.user?.id)
        .single();

      const { data, error } = await supabase
        .from('knowledge_base_articles')
        .update({
          ...updates,
          updated_by: appUser?.id,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as KBArticle;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['kb-articles'] });
      queryClient.invalidateQueries({ queryKey: ['kb-article', data.slug] });
      queryClient.invalidateQueries({ queryKey: ['kb-categories'] });
      queryClient.invalidateQueries({ queryKey: ['kb-playbooks'] });
      queryClient.invalidateQueries({ queryKey: ['kb-playbook-children'] });
    },
  });
};

// Delete article
export const useDeleteArticle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('knowledge_base_articles')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb-articles'] });
      queryClient.invalidateQueries({ queryKey: ['kb-categories'] });
      queryClient.invalidateQueries({ queryKey: ['kb-playbooks'] });
      queryClient.invalidateQueries({ queryKey: ['kb-playbook-children'] });
    },
  });
};
